import { useEffect, useState } from 'react';

import {
  getPartnerExportRefusal,
  transcriptorApi,
  type PartnerDocumentExport,
  type PartnerExportRefusal,
} from '@/shared/api/transcriptor';

import { PARTNER_EXPORT_DELAY_MS } from '../config';

type Fetched =
  | { key: string; result: PartnerDocumentExport }
  | { key: string; result: null; refusal?: PartnerExportRefusal };

export type PartnerExportState =
  | { status: 'listening' }
  | { status: 'loading' }
  | { status: 'ready'; result: PartnerDocumentExport }
  | { status: 'failed'; refusal?: PartnerExportRefusal };

// A partner template's note as the partner's text, read again for every
// answers revision the server acknowledged: a copy then writes the clipboard
// within the click, which Safari requires, instead of after a request. While
// the live fill or unsent edits still change the note, nothing is read; a
// burst of revisions is read once, after a pause.
export const usePartnerExport = ({
  encounterId,
  documentId,
  revision,
  listening,
  editsPending,
}: {
  encounterId: string;
  documentId: string;
  revision: number;
  listening: boolean;
  editsPending: boolean;
}): PartnerExportState & { retry: () => void } => {
  const [fetched, setFetched] = useState<Fetched | null>(null);
  const [attempt, setAttempt] = useState(0);
  const key = [encounterId, documentId, revision, attempt].join(':');
  const fetchedKey = fetched?.key;
  const waiting = listening || editsPending;

  useEffect(() => {
    if (waiting || fetchedKey === key) {
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      transcriptorApi
        .exportPartnerDocument({
          encounterId,
          documentId,
          signal: controller.signal,
        })
        .then(
          (result) => {
            if (!controller.signal.aborted) {
              setFetched({ key, result });
            }
          },
          (error: unknown) => {
            if (!controller.signal.aborted) {
              setFetched({
                key,
                result: null,
                refusal: getPartnerExportRefusal(error),
              });
            }
          },
        );
    }, PARTNER_EXPORT_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [encounterId, documentId, key, waiting, fetchedKey]);

  const retry = () => setAttempt((current) => current + 1);

  if (listening) {
    return { status: 'listening', retry };
  }
  if (editsPending || fetched?.key !== key) {
    return { status: 'loading', retry };
  }
  return fetched.result
    ? { status: 'ready', result: fetched.result, retry }
    : { status: 'failed', refusal: fetched.refusal, retry };
};
